/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ExternalLink, FileSearch, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { DeclarationTemplate, TemplateRow, TrustAnchor } from '@/api/areas/declarations';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n';
import { useApi } from '@/lib/useApi';
import { ActionDialog } from '../compliance/ui';
import { FileFrame } from './shared';

function TemplateCard({ t: tpl }: { t: DeclarationTemplate }) {
  const { t } = useTranslation('declarations');
  const ta = useTranslation('accounts').t as unknown as (key: string, o: { defaultValue: string }) => string;
  const lang = useLanguage();
  const [preview, setPreview] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const rows = tpl.rows.filter((r) => !r.heading);
  const columns: Column<TemplateRow>[] = [
    { key: 'label', header: t('templates.label'), cell: (r) => r.label },
    { key: 'key', header: t('templates.key'), cell: (r) => (r.key ? <code className="text-xs">{r.key}</code> : '—') },
    {
      key: 'source',
      header: t('templates.binding'),
      cell: (r) => (r.text ? <span className="text-xs text-muted-foreground">{t('templates.fixedText')}</span> : r.source === 'none' ? <span className="text-xs text-muted-foreground">{t('templates.blank')}</span> : <code className="text-xs break-all">{r.source}</code>),
    },
    {
      key: 'flags',
      header: '',
      cell: (r) => (
        <span className="flex flex-wrap gap-1">
          {r.required ? <Badge tone="info" icon={false}>{t('templates.required')}</Badge> : null}
          {r.compare ? <Badge tone="neutral" icon={false} title={r.compare}>{t('templates.compare')}</Badge> : null}
          {r.sensitive ? <Badge tone="warn" icon={false}>{t('templates.sensitive')}</Badge> : null}
        </span>
      ),
    },
  ];
  return (
    <Section
      flush
      title={lang === 'vi' ? tpl.name_vi : tpl.name_en}
      description={tpl.legal_basis}
      actions={
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              setPreview(await api.declarationTemplatePreview(tpl.code));
            } catch (e) {
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          <FileSearch /> {t('templates.preview')}
        </Button>
      }
    >
      <div className="grid gap-4 px-5 pb-4">
        <KeyValue
          columns={3}
          rows={[
            [t('templates.form'), tpl.form],
            [t('templates.version'), <code key="v" className="text-xs">{`${tpl.code}@${tpl.version}`}</code>],
            [t('templates.file'), <code key="f" className="text-xs">{tpl.file}</code>],
            [t('templates.subjectTypes'), tpl.subject_types.map((x) => ta(`enum.subjectType.${x}`, { defaultValue: x })).join(', ')],
            [
              t('templates.source'),
              <a key="s" href={tpl.source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                {tpl.source.pages ? `p. ${tpl.source.pages}` : tpl.source.url} <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>,
            ],
            [
              t('templates.signature'),
              tpl.signature.title,
            ],
          ]}
        />
        {tpl.source.layout_checked ? <Callout tone="ok">{t('templates.layoutChecked')}</Callout> : <Callout tone="warn" title={t('templates.layoutNotChecked')}>{tpl.source.note}</Callout>}
        {error ? <WriteError error={error} /> : null}
      </div>
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.key ?? r.label ?? ''} />
      <ActionDialog open={preview !== null} onOpenChange={(o) => (o ? undefined : setPreview(null))} title={`${tpl.form} · ${tpl.version}`} wide>
        <FileFrame blob={preview} title={tpl.code} />
      </ActionDialog>
    </Section>
  );
}

/** Cài đặt › Tên miền & TLD › Mẫu bản khai: read-only, defined in `config/declarations`. */
export function DeclarationTemplatesPage() {
  const { t } = useTranslation('declarations');
  useDocumentTitle(t('templates.title'));
  const result = useApi((signal) => api.listDeclarationTemplates({ signal }), []);
  const anchorColumns: Column<TrustAnchor>[] = [
    { key: 'subject', header: t('templates.trustSubject'), cell: (a) => <span className="text-sm">{a.subject}</span> },
    { key: 'file', header: t('templates.trustFile'), cell: (a) => <code className="text-xs">{a.file}</code> },
    { key: 'sha', header: t('templates.trustFingerprint'), cell: (a) => <code className="text-xs break-all">{a.sha256}</code> },
  ];
  return (
    <>
      <PageHeader title={t('templates.title')} subtitle={t('templates.description')} />
      {result.status === 'loading' ? <Loading rows={4} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('templates.loadError')} /> : null}
      {result.status === 'success' ? (
        <div className="grid min-w-0 grid-cols-1 gap-6">
          <Callout tone="info">{t('templates.fileDefined', { source: result.data.source })}</Callout>
          {result.data.templates.map((tpl) => (
            <TemplateCard key={tpl.code} t={tpl} />
          ))}
          <Section flush title={t('templates.trustTitle')} description={t('templates.trustDescription', { dir: result.data.trust_store.dir })}>
            <DataTable
              columns={anchorColumns}
              rows={result.data.trust_store.anchors}
              rowKey={(a) => a.sha256}
              empty={
                <EmptyState title={t('templates.trustEmpty')} icon={ShieldCheck}>
                  {result.data.trust_store.dir}
                </EmptyState>
              }
            />
          </Section>
        </div>
      ) : null}
    </>
  );
}
